/**
 * Analyzer: Setup × Confluence Fit
 *
 * For a given setup, walks each confluence that appears in its trade
 * history and asks: "is this confluence the edge of this setup, or is it
 * just along for the ride?"
 *
 * Two patterns:
 *
 *   1. EDGE: "Above All EMAs" pushes MMXM from 35% WR (without) to 78%
 *      (with). Meaningful gap on adequate sample → emit Pin action so the
 *      trader can one-click add it as a required confluence for the setup.
 *
 *   2. DRAG: "Choppy/Erratic" already pinned but tags trades that fail
 *      (WR with < WR without by ≥ threshold). Warning observation,
 *      action text suggests removing or gating the recommendation.
 *
 * Different from `confluenceConsensus` (session-scope, looks at universal
 * presence on every trade in one group): this one is setup-scope, walks
 * the setup's full history, and asks WHICH confluences move WR — not
 * which ones happened to be on every trade today.
 *
 * Different from the existing analytics card rendering: the analytics
 * shows raw stats, this analyzer narrates them and ships actionable
 * pin/drop commands the trader can apply with one click.
 *
 * Reads from ctx:
 *   setupName, setupTrades                      — provided by setup scope
 *   allConfluences                              — for id → label resolution
 *   currentRecommendedConfluences               — to dedupe pin recommendations
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../../types.js';

export const key = 'setupConfluenceFit';
export const tier = 'free';

// Minimum sample size on each side (with-conf / without-conf) before we
// trust a WR gap. Same conservative bar as setupBarContext's stack-edge.
const MIN_TRADES_PER_SIDE = 3;
// How big a WR gap before we claim it's signal, not noise.
const MIN_WR_GAP = 0.20;
// Don't recommend pinning a confluence that already appears in less than
// half of the setup's trades — too rare to be a setup-level filter.
const MIN_PRESENCE_FRAC_FOR_PIN = 0.30;

const fmtPct = (n) => `${Math.round(n * 100)}%`;

const resolveName = (id, lib = []) => {
  const hit = lib.find(c => c.id === id || c.name === id);
  return hit?.name || id;
};

/**
 * For each confluence id present on at least one of the setup's trades,
 * compute (WR with, WR without, sample sizes) and rank by absolute gap.
 * Returns the per-confluence stats sorted by signal strength.
 */
const computeConfluenceFit = (setupTrades) => {
  const confIds = new Set();
  for (const t of setupTrades) {
    if (Array.isArray(t?.confluences)) {
      for (const id of t.confluences) confIds.add(id);
    }
  }
  if (confIds.size === 0) return [];

  const total = setupTrades.length;
  const out = [];
  for (const id of confIds) {
    const withConf    = setupTrades.filter(t => Array.isArray(t.confluences) && t.confluences.includes(id));
    const withoutConf = setupTrades.filter(t => !Array.isArray(t.confluences) || !t.confluences.includes(id));
    if (withConf.length < MIN_TRADES_PER_SIDE) continue;
    if (withoutConf.length < MIN_TRADES_PER_SIDE) continue;

    const wr = (group) => group.length === 0 ? 0
      : group.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length / group.length;
    const withWR    = wr(withConf);
    const withoutWR = wr(withoutConf);
    const gap = withWR - withoutWR;
    const presence = withConf.length / total;

    out.push({
      id,
      withCount: withConf.length,
      withoutCount: withoutConf.length,
      withWR,
      withoutWR,
      gap,                    // signed: positive = with-conf better, negative = drag
      absGap: Math.abs(gap),
      presence,
    });
  }

  // Strongest absolute gap first
  return out.sort((a, b) => b.absGap - a.absGap);
};

// ── Public analyze() ──────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const {
    setupName,
    setupTrades,
    allConfluences = [],
    currentRecommendedConfluences = [],
  } = ctx || {};
  if (!setupName || !Array.isArray(setupTrades) || setupTrades.length === 0) return null;

  const stats = computeConfluenceFit(setupTrades);
  if (stats.length === 0) return null;

  const recommendedSet = new Set(currentRecommendedConfluences);
  const observations = [];

  for (const s of stats) {
    if (s.absGap < MIN_WR_GAP) continue;

    const confLabel = resolveName(s.id, allConfluences);
    const isAlreadyPinned = recommendedSet.has(s.id);

    if (s.gap >= MIN_WR_GAP && s.presence >= MIN_PRESENCE_FRAC_FOR_PIN && !isAlreadyPinned) {
      // EDGE pattern — actionable. Recommend pinning.
      observations.push(makeObservation({
        id: `${key}:${setupName}:edge:${s.id}`,
        analyzer: key,
        scope: 'setup',
        scopeId: setupName,
        severity: SEVERITY.SUCCESS,
        category: CATEGORY.CONFLUENCE,
        title: `"${confLabel}" carries "${setupName}" — ${fmtPct(s.withWR)} WR vs ${fmtPct(s.withoutWR)} without`,
        detail: `${s.withCount} trades with this confluence: ${fmtPct(s.withWR)} WR. ${s.withoutCount} without: ${fmtPct(s.withoutWR)} WR. Gap of ${fmtPct(s.absGap)}.`,
        action: `Pin this as a required confluence on "${setupName}". Skip the setup when "${confLabel}" isn't present.`,
        actions: [
          makeAction({
            type: ACTION_TYPE.PIN_CONFLUENCE_TO_SETUP,
            label: `Pin "${confLabel}"`,
            payload: {
              setupName,
              confluenceIds: [s.id],
              confluenceLabels: [confLabel],
            },
            confirmText: `Pin "${confLabel}" as a required confluence on "${setupName}"?`,
            doneLabel: 'Pinned ✓',
          }),
        ],
        evidence: { confluenceId: s.id, withWR: s.withWR, withoutWR: s.withoutWR, gap: s.gap, presence: s.presence },
        confidence: 0.84,
        priority: 80,
      }));
    } else if (s.gap <= -MIN_WR_GAP && isAlreadyPinned) {
      // DRAG pattern — confluence is currently RECOMMENDED but tagging the
      // setup's losers more than its winners. Warn and suggest review.
      observations.push(makeObservation({
        id: `${key}:${setupName}:drag:${s.id}`,
        analyzer: key,
        scope: 'setup',
        scopeId: setupName,
        severity: SEVERITY.WARNING,
        category: CATEGORY.CONFLUENCE,
        title: `"${confLabel}" is dragging on "${setupName}" — ${fmtPct(s.withWR)} WR vs ${fmtPct(s.withoutWR)} without`,
        detail: `${s.withCount} trades with this confluence: ${fmtPct(s.withWR)} WR. ${s.withoutCount} without: ${fmtPct(s.withoutWR)} WR. Pinned as recommended but underperforming.`,
        action: `Review whether "${confLabel}" is actually a filter or just along for the ride. Consider unpinning or gating it.`,
        evidence: { confluenceId: s.id, withWR: s.withWR, withoutWR: s.withoutWR, gap: s.gap, presence: s.presence },
        confidence: 0.78,
        priority: 74,
      }));
    } else if (s.gap <= -MIN_WR_GAP && !isAlreadyPinned) {
      // Anti-edge: confluence consistently appears on losing trades but isn't
      // pinned. Useful informational signal — "watch out, this thing tags
      // your losers." Lower priority than the EDGE pattern.
      observations.push(makeObservation({
        id: `${key}:${setupName}:anti-edge:${s.id}`,
        analyzer: key,
        scope: 'setup',
        scopeId: setupName,
        severity: SEVERITY.INFO,
        category: CATEGORY.CONFLUENCE,
        title: `"${confLabel}" tags "${setupName}" losers — ${fmtPct(s.withWR)} WR vs ${fmtPct(s.withoutWR)} without`,
        detail: `${s.withCount} trades with this confluence: ${fmtPct(s.withWR)} WR. ${s.withoutCount} without: ${fmtPct(s.withoutWR)} WR. Treat it as a soft "skip" signal.`,
        action: `Watch for "${confLabel}" as a yellow-light tell. Pair-check it against other context before pulling trigger.`,
        evidence: { confluenceId: s.id, withWR: s.withWR, withoutWR: s.withoutWR, gap: s.gap, presence: s.presence },
        confidence: 0.70,
        priority: 60,
      }));
    }
  }

  return observations.length === 0 ? null : observations;
};

// Exports for tests
export { computeConfluenceFit };
